import clsx from "clsx";
import PropTypes from "prop-types";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { alpha, Box, Button, Icon } from "@mui/material";
import { makeStyles } from "@mui/styles";
import CustomSelect from "../../components/common/CustomSelect";
import { SearchTextInput } from "../../components/common/SearchTextInput";
import WorkspaceSelector from "../../components/common/WorkspaceSelector";

const useStyles = makeStyles(theme => ({
  paperHead: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #ddd",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  btn: {
    padding: "10px 15px",
    fontWeight: 600,
    lineHeight: 0.5,
    color: theme.palette.primary.contrastText,
    fontSize: 12,
    backgroundColor: theme.palette.primary.main,
    minWidth: "unset",
    borderRadius: 4,
    marginLeft: 10,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.8),
    },
    width: "auto",
    [theme.breakpoints.down("sm")]: {
      marginTop: 10,
      marginBottom: 0,
      marginLeft: 0,
    },
  },
  btnIcon: {
    fontSize: 12,
    marginLeft: 10,
    width: "unset",
    height: "unset",
    overflow: "unset",
    verticalAlign: "middle",
  },
  selectWrap: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      marginTop: 30,
    },
  },
  popper: {
    width: 280,
  },
  btnDisabled: {
    opacity: 0.8,
    "&.$Mui-disabled": {
      color: theme.palette.primary.contrastText,
    },
  },
  select: {
    marginLeft: 7,
    minWidth: 160,
  },
  searchTextInput: {
    marginLeft: 7,
    marginTop: 0,
    minWidth: 300,
  },
}));

const SiteUserHeader = ({
  filter,
  filterOptions,
  handleOpenInviteUser,
  handleOpenManageRoles,
  loadingUsers,
  onSearchUsers,
  onSiteChanged,
  selectedWorkspaceName,
  setFilter,
  siteRolesRights,
}) => {
  const classes = useStyles();

  const workspaces = useSelector(state => state.api.currentViewer.viewer.structure.items);

  const [openWorkspaceSelector, setOpenWorkspaceSelector] = useState(false);

  const { canInviteUsers, canManageSiteRoles } = siteRolesRights || {};

  const selectWorkspace = (siteId) => {
    onSiteChanged(siteId);
    setOpenWorkspaceSelector(false);
  };

  return (
    <Box className={classes.paperHead}>
      <div className={classes.popper}>
        <WorkspaceSelector
          handleToggle={_ => setOpenWorkspaceSelector(prev => !prev)}
          onClick={selectWorkspace}
          open={openWorkspaceSelector}
          searchable
          setOpen={setOpenWorkspaceSelector}
          workspaceName={selectedWorkspaceName}
          workspaces={workspaces}
        />
      </div>

      {canInviteUsers && (
        <Button
          classes={{ disabled: classes.btnDisabled }}
          className={classes.btn}
          onClick={handleOpenInviteUser}
        >
          <FormattedMessage id="btn.inviteUsers" />
          <Icon className={clsx(["fa fa-user-plus", classes.btnIcon])} />
        </Button>
      )}

      {canManageSiteRoles && (
        <Button
          classes={{ disabled: classes.btnDisabled }}
          className={classes.btn}
          onClick={handleOpenManageRoles}
        >
          <FormattedMessage id="btn.manageRoles" />
          <Icon className={clsx(["fa fa-user-cog", classes.btnIcon])} />
        </Button>
      )}

      <Box className={classes.selectWrap}>
        <CustomSelect
          customClass={classes.select}
          inputLabel={<FormattedMessage id="siteUsers.filter" />}
          onChange={e => setFilter(e.target.value)}
          options={filterOptions}
          value={filter}
        />
        <SearchTextInput
          className={classes.searchTextInput}
          onChange={onSearchUsers}
          searching={loadingUsers}
        />
      </Box>
    </Box>
  );
};

SiteUserHeader.defaultProps = {
  filter: "",
  loadingUsers: false,
  siteRolesRights: {},
};

SiteUserHeader.propTypes = {
  filter: PropTypes.string,
  filterOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleOpenInviteUser: PropTypes.func.isRequired,
  handleOpenManageRoles: PropTypes.func.isRequired,
  loadingUsers: PropTypes.bool.isRequired,
  onSearchUsers: PropTypes.func.isRequired,
  onSiteChanged: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  siteRolesRights: PropTypes.shape({}),
};

export default SiteUserHeader;
